import { useRef, useState } from 'react';
import { FaEnvelope, FaLock, FaUser } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef({});
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const focusField = (fieldName) => {
    const input = inputRefs.current[fieldName];
    if (input && typeof input.focus === 'function') {
      input.focus();
    }
  };

  const getFirstValidationError = (result) => {
    if (!Array.isArray(result?.errors) || result.errors.length === 0) {
      return null;
    }

    const firstError = result.errors.find((error) => error?.path || error?.param) || result.errors[0];
    const field = firstError?.path || firstError?.param;
    const message = firstError?.msg || result.message;

    if (!field || !message) {
      return null;
    }

    return { field, message };
  };

  const handleChange = (e) => {
    if (fieldErrors[e.target.name]) {
      setFieldErrors((prev) => {
        const updated = { ...prev };
        delete updated[e.target.name];
        return updated;
      });
    }

    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    setLoading(true);

    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      toast.success('Login successful!');
      navigate('/');
    } else {
      const validationError = getFirstValidationError(result);
      if (validationError) {
        setFieldErrors({ [validationError.field]: validationError.message });
        focusField(validationError.field);
        toast.error(validationError.message);
        setLoading(false);
        return;
      }

      toast.error(result.message || 'Login failed');
    }
    
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setFieldErrors({});

    if (formData.password !== formData.confirmPassword) {
      setFieldErrors({ confirmPassword: 'Passwords do not match' });
      focusField('confirmPassword');
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setFieldErrors({ password: 'Password must be at least 6 characters' });
      focusField('password');
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    const result = await register(formData.username, formData.email, formData.password);
    
    if (result.success) {
      toast.success('Registration successful!');
      navigate('/');
    } else {
      const validationError = getFirstValidationError(result);
      if (validationError) {
        setFieldErrors({ [validationError.field]: validationError.message });
        focusField(validationError.field);
        toast.error(validationError.message);
        setLoading(false);
        return;
      }

      toast.error(result.message || 'Registration failed');
    }
    
    setLoading(false);
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8">
          <h1 className="text-3xl font-bold text-center text-gray-900 mb-8">
            {isLogin ? 'Login' : 'Create Account'}
          </h1>

          {/* Toggle between Login and Register */}
          <div className="flex gap-4 mb-6 border-b">
            <button
              onClick={() => {
                setIsLogin(true);
                setFormData({ username: '', email: '', password: '', confirmPassword: '' });
                setFieldErrors({});
              }}
              className={`flex-1 py-2 font-medium transition ${
                isLogin
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-600 hover:text-primary'
              }`}
            >
              Login
            </button>
            <button
              onClick={() => {
                setIsLogin(false);
                setFormData({ username: '', email: '', password: '', confirmPassword: '' });
                setFieldErrors({});
              }}
              className={`flex-1 py-2 font-medium transition ${
                !isLogin
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-600 hover:text-primary'
              }`}
            >
              Register
            </button>
          </div>

          {isLogin ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaEnvelope className="inline mr-2" />
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  ref={(input) => {
                    inputRefs.current.email = input;
                  }}
                  required
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                    fieldErrors.email ? 'border-red-500' : ''
                  }`}
                  placeholder="your.email@example.com"
                />
                {fieldErrors.email && <p className="text-red-600 text-sm mt-1">{fieldErrors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaLock className="inline mr-2" />
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  ref={(input) => {
                    inputRefs.current.password = input;
                  }}
                  required
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                    fieldErrors.password ? 'border-red-500' : ''
                  }`}
                  placeholder="Enter your password"
                />
                {fieldErrors.password && <p className="text-red-600 text-sm mt-1">{fieldErrors.password}</p>}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-white py-3 rounded-lg hover:bg-primary-dark transition font-semibold disabled:opacity-50"
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaUser className="inline mr-2" />
                  Username
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  ref={(input) => {
                    inputRefs.current.username = input;
                  }}
                  required
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                    fieldErrors.username ? 'border-red-500' : ''
                  }`}
                  placeholder="Choose a username"
                />
                {fieldErrors.username && <p className="text-red-600 text-sm mt-1">{fieldErrors.username}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaEnvelope className="inline mr-2" />
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  ref={(input) => {
                    inputRefs.current.email = input;
                  }}
                  required
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                    fieldErrors.email ? 'border-red-500' : ''
                  }`}
                  placeholder="your.email@example.com"
                />
                {fieldErrors.email && <p className="text-red-600 text-sm mt-1">{fieldErrors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaLock className="inline mr-2" />
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  ref={(input) => {
                    inputRefs.current.password = input;
                  }}
                  required
                  minLength={6}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                    fieldErrors.password ? 'border-red-500' : ''
                  }`}
                  placeholder="At least 6 characters"
                />
                {fieldErrors.password && <p className="text-red-600 text-sm mt-1">{fieldErrors.password}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaLock className="inline mr-2" />
                  Confirm Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  ref={(input) => {
                    inputRefs.current.confirmPassword = input;
                  }}
                  required
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                    fieldErrors.confirmPassword ? 'border-red-500' : ''
                  }`}
                  placeholder="Confirm your password"
                />
                {fieldErrors.confirmPassword && <p className="text-red-600 text-sm mt-1">{fieldErrors.confirmPassword}</p>}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-white py-3 rounded-lg hover:bg-primary-dark transition font-semibold disabled:opacity-50"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/" className="text-primary hover:underline">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;

